import { Box, CircularProgress, Stack, SxProps, Theme, Typography } from "@mui/material";
import { isFunction, isInteger } from "lodash-es";
import { useCallback, useMemo } from "react";

import Image from "@/common/components/Images/Image";
import { cleanlinessTags, damageTags } from "@/common/constants/vehicleInsightsTags";
import { FileItem } from "@/common/fileItem";
import { FileHelper } from "@/common/helpers/file";
import { TextHelper } from "@/common/helpers/text";
import { enumService } from "@/common/services/enum";
import { ThumbnailSizeType } from "@/core/api/generated/v1";
import { VehicleCleanlinessExtent, VehicleDamageExtent } from "@/core/api/generated/v1/api";

import AppIconButton from "../Button/AppIconButton";
import AppIcon from "../Icons/AppIcon";
import AppTooltip from "../Tooltip/AppTooltip";
import AttachmentCaptionInput from "./AttachmentCaptionInput";
import FileIcon from "./FileIcon";

export interface FileItemViewActions {
  enabled: boolean;
  click?: boolean;
  quickView?: boolean;
  edit?: boolean;
  download?: boolean;
  openInNewTab?: boolean;
  remove?: boolean;
  setCaption?: boolean;
  onClick?: (fileItem: FileItem) => void;
  onQuickView?: (fileItem: FileItem) => void;
  onEdit?: (fileItem: FileItem) => void;
  onRemove?: (fileItem: FileItem) => void;
  onSetCaption?: (caption: string | undefined) => void;
}

export interface FileItemViewProps {
  fileItem: FileItem;
  sizeVariant?: "normal" | "compact";
  /** Use size instead of sx to correctly select thumbnails. */
  size?: {
    width?: number | "auto" | "unset";
    height?: number | "auto" | "unset";
  };
  useThumbnail?: boolean;
  thumbnailSizeType?: ThumbnailSizeType;
  thumbnailSize?: IImageSize;
  fullWidth?: boolean;
  withCaption?: boolean;
  sx?: SxProps<Theme>;
  imageSx?: SxProps<Theme>;
  actions?: FileItemViewActions;
  showAIInsightsTags?: boolean;
}

export default function FileItemView({
  fileItem,
  sizeVariant = "normal",
  size = { width: undefined, height: undefined },
  useThumbnail = false,
  thumbnailSizeType,
  thumbnailSize,
  actions,
  fullWidth,
  withCaption = true,
  sx,
  imageSx,
  showAIInsightsTags = true,
}: FileItemViewProps) {
  // Vehicle insights extraction
  const vehicleInsights =
    fileItem.attachment?.vehicleInsights?.status === "Success"
      ? fileItem.attachment?.vehicleInsights?.value
      : undefined;

  const showCleanliness =
    vehicleInsights?.vehicleCleanlinessExtent !== undefined &&
    cleanlinessTags.includes(vehicleInsights.vehicleCleanlinessExtent);

  const showDamage =
    vehicleInsights?.vehicleDamageExtent !== undefined &&
    damageTags.includes(vehicleInsights.vehicleDamageExtent);

  // Prepare tag values
  const cleanlinessTag = showCleanliness
    ? TextHelper.textToAbbreviation(
        enumService.getEnumValueName(
          "VehicleCleanlinessExtent",
          vehicleInsights.vehicleCleanlinessExtent,
        ),
      )
    : null;
  const damageTag = showDamage
    ? TextHelper.textToAbbreviation(
        enumService.getEnumValueName("VehicleDamageExtent", vehicleInsights.vehicleDamageExtent),
      )
    : null;

  const actionsComputed = useMemo<FileItemViewActions | undefined>(
    () => (isFunction(actions) ? actions(fileItem) : actions),
    [actions],
  );

  const fileType = FileHelper.getFileType(fileItem.mimeType);
  const canShowActions = fileItem.isUploading || actionsComputed?.enabled;
  const canShowThumbnail = useThumbnail && fileItem.hasThumbnails;
  const thumbnailUrl = canShowThumbnail
    ? fileItem.getThumbnailUrl({
        sizeType: thumbnailSizeType,
        minSize: thumbnailSize,
        maxSize:
          isInteger(size?.width) || isInteger(size?.height)
            ? {
                width: isInteger(size?.width) ? (size!.width! as number) : undefined,
                height: isInteger(size?.height) ? (size!.height! as number) : undefined,
              }
            : undefined,
      })
    : undefined;

  const fileUrlComputed = useMemo(() => {
    return fileItem.fileUrl;
  }, [fileItem.id, fileItem.fileUrl]);

  const handleQuickView = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      actionsComputed?.onQuickView && actionsComputed?.onQuickView(fileItem);
    },
    [actionsComputed, fileItem],
  );
  const handleEdit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      actionsComputed?.onEdit && actionsComputed?.onEdit(fileItem);
    },
    [actionsComputed, fileItem],
  );

  const handleDownload = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      fileItem.download();
    },
    [fileItem],
  );

  const handleOpenInNewTab = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (fileItem.fileUrl && fileItem.displayFileName) {
        FileHelper.openFileByUrl(fileItem.fileUrl, fileItem.displayFileName);
      }
    },
    [fileItem],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      actionsComputed?.click && actionsComputed?.onClick && actionsComputed.onClick(fileItem);
    },
    [actionsComputed, fileItem],
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      actionsComputed?.onRemove && actionsComputed?.onRemove(fileItem);
    },
    [actionsComputed, fileItem],
  );

  const handleSetCaption = useCallback(
    (caption: string | undefined) => {
      actionsComputed?.onSetCaption && actionsComputed?.onSetCaption(caption);
    },
    [actionsComputed, fileItem],
  );

  return (
    <AppTooltip
      isEnabled={withCaption}
      title={fileItem.attachment?.caption || fileItem.displayFileName}
      isDefaultEnterDelays
    >
      <Box
        sx={{
          position: "relative",
          width: fileType.isImage ? size.width : "auto",
          height: fileType.isImage ? size.height : "auto",
          minHeight: "30px",
          minWidth: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: (th) =>
            fileType.isImage ? th.shapeCustom.borderRadiusImage : th.shapeCustom.borderRadiusFile,
          borderColor: (th) => (fileItem.validation ? th.palette.error.main : th.palette.divider),
          "&:hover": {
            "& .controls": {
              visibility: "visible",
            },
            "& .validationControls": {
              visibility: "visible",
            },
          },
          cursor: actionsComputed?.click ? "pointer" : "auto",
          overflow: "hidden",
          ...(sx || {}),
        }}
        data-file-item-view='true'
        onClick={actionsComputed?.click ? (e) => handleClick(e) : undefined}
      >
        {/* Vehicle insights tags (top right corner) */}
        {showAIInsightsTags && (
          <>
            {(showCleanliness || showDamage) && (
              <Stack
                direction='row'
                spacing={0.5}
                sx={{
                  position: "absolute",
                  bottom: 2,
                  right: 2,
                  zIndex: 0,
                }}
              >
                {showCleanliness && (
                  <AppTooltip
                    title={enumService.getEnumValueName(
                      "VehicleCleanlinessExtent",
                      vehicleInsights.vehicleCleanlinessExtent || "None",
                    )}
                    placement='top'
                    isDefaultEnterDelays
                  >
                    <Box
                      sx={{
                        width: 25,
                        aspectRatio: "1 / 1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor:
                          vehicleInsights.vehicleCleanlinessExtent ===
                            VehicleCleanlinessExtent.VeryDirty ||
                          vehicleInsights.vehicleCleanlinessExtent ===
                            VehicleCleanlinessExtent.ExtremelyDirty
                            ? "error.main"
                            : "warning.main",
                        color: "warning.contrastText",
                        borderRadius: "8px",
                        fontSize: 10,
                        fontWeight: 500,
                      }}
                    >
                      {cleanlinessTag}
                    </Box>
                  </AppTooltip>
                )}
                {showDamage && (
                  <AppTooltip
                    title={enumService.getEnumValueName(
                      "VehicleDamageExtent",
                      vehicleInsights.vehicleDamageExtent || "None",
                    )}
                    placement='top'
                    isDefaultEnterDelays
                  >
                    <Box
                      sx={{
                        width: 25,
                        aspectRatio: "1 / 1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor:
                          vehicleInsights.vehicleDamageExtent === VehicleDamageExtent.Severe ||
                          vehicleInsights.vehicleDamageExtent === VehicleDamageExtent.VerySevere
                            ? "error.main"
                            : "warning.main",
                        color: "error.contrastText",
                        borderRadius: "8px",
                        fontSize: 10,
                        fontWeight: 500,
                      }}
                    >
                      {damageTag}
                    </Box>
                  </AppTooltip>
                )}
              </Stack>
            )}
          </>
        )}

        {/* Thumbnail */}
        {canShowThumbnail && (
          <Image sx={{ width: "unset", height: "100%", ...(imageSx || {}) }} src={thumbnailUrl} />
        )}

        {/* Image */}
        {!canShowThumbnail && fileType.isImage && (
          <Image
            sx={{ width: "unset", height: "100%", ...(imageSx || {}) }}
            src={fileUrlComputed}
          />
        )}

        {/* Other files */}
        {!canShowThumbnail && !fileType.isImage && (
          <Stack
            direction='row'
            spacing={1}
            sx={{
              alignItems: "center",
              maxWidth: fullWidth ? "unset" : "250px",
              height: "100%",
              overflow: "hidden",
              backgroundColor: "background.default",
              ...(sizeVariant === "normal" && { py: 1.5, px: 2.0 }),
              ...(sizeVariant === "compact" && { py: 1, px: 1.5 }),
            }}
          >
            <FileIcon mimeType={fileItem.mimeType} fontSize='medium' />
            {(sizeVariant === "normal" || (canShowActions && fileItem.isUploading)) && (
              <Stack direction='row' spacing={1} sx={{ overflow: "hidden", alignItems: "center" }}>
                {sizeVariant === "normal" && (
                  <Typography
                    component='span'
                    variant='body1'
                    color='text.secondary'
                    sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
                  >
                    {fileItem.displayFileName}
                  </Typography>
                )}

                {canShowActions && (
                  <>
                    {fileItem.isUploading && (
                      <AppTooltip title='Cancel upload' placement='top' isDefaultEnterDelays>
                        <AppIconButton
                          sx={{ padding: "2px" }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            fileItem.abortUpload && fileItem.abortUpload();
                          }}
                          size='extraSmall'
                        >
                          <AppIcon of='clear' />
                        </AppIconButton>
                      </AppTooltip>
                    )}
                  </>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {/* Errors */}
        {fileItem.validation && (
          <Stack
            className='validationControls'
            sx={{
              visibility: "collapse",
              position: "absolute",
              left: 5,
              top: 3,
              transform: "scale(1) translate(0%, 0%)",
              borderRadius: (t) => t.shapeCustom.borderRadiusButton,
              backgroundColor: (theme) => theme.palette.background.default,
            }}
            direction='row'
            spacing='4px'
          >
            <AppTooltip
              title={`Upload failed. ${fileItem.validation?.generalError}`}
              placement='top'
              isDefaultEnterDelays
            >
              <AppIconButton
                sx={{ p: 0, m: 0 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  fileItem.showErrorDetails && fileItem.showErrorDetails(fileItem.validation!);
                }}
              >
                <AppIcon of='error' color='error' fontSize='medium' />
              </AppIconButton>
            </AppTooltip>

            {fileItem.retryUpload && (
              <AppTooltip title='Re-try uploading' placement='top' isDefaultEnterDelays>
                <AppIconButton
                  sx={{ p: 0, m: 0 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    fileItem.retryUpload && fileItem.retryUpload();
                  }}
                >
                  <AppIcon of='replay' fontSize='medium' />
                </AppIconButton>
              </AppTooltip>
            )}
            <AppTooltip title='Remove' placement='top' isDefaultEnterDelays>
              <AppIconButton size='extraSmall' onClick={handleRemove}>
                <AppIcon of='clear' />
              </AppIconButton>
            </AppTooltip>
          </Stack>
        )}

        {/* Actions */}
        {!fileItem.validation &&
          canShowActions &&
          (() => {
            const loader = fileItem.isUploading && (
              <Stack alignItems='center' justifyContent='center'>
                <CircularProgress size={16} />
              </Stack>
            );
            const cancelUpload = fileItem.isUploading && (
              <AppTooltip title='Cancel upload' placement='top' isDefaultEnterDelays>
                <AppIconButton
                  sx={{ padding: "2px" }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    fileItem.abortUpload && fileItem.abortUpload();
                  }}
                  size='extraSmall'
                >
                  <AppIcon of='clear' />
                </AppIconButton>
              </AppTooltip>
            );
            const quickView = !fileItem.isUploading && actionsComputed?.quickView && (
              <AppTooltip title='Quick view' placement='top' isDefaultEnterDelays>
                <AppIconButton size='extraSmall' onClick={handleQuickView}>
                  <AppIcon of='quickView' />
                </AppIconButton>
              </AppTooltip>
            );
            const edit = !fileItem.isUploading && actionsComputed?.edit && (
              <AppTooltip title='Edit' placement='top' isDefaultEnterDelays>
                <AppIconButton size='extraSmall' onClick={handleEdit}>
                  <AppIcon of='edit' />
                </AppIconButton>
              </AppTooltip>
            );
            const download = !fileItem.isUploading && actionsComputed?.download && (
              <AppTooltip title='Download' placement='top' isDefaultEnterDelays>
                <AppIconButton size='extraSmall' onClick={handleDownload}>
                  <AppIcon of='download' />
                </AppIconButton>
              </AppTooltip>
            );
            const openInNewTab = !fileItem.isUploading && actionsComputed?.openInNewTab && (
              <AppTooltip title='Open in new tab' placement='top' isDefaultEnterDelays>
                <AppIconButton size='extraSmall' onClick={handleOpenInNewTab}>
                  <AppIcon of='openInNew' />
                </AppIconButton>
              </AppTooltip>
            );
            const setCaption = !fileItem.isUploading &&
              actionsComputed?.setCaption &&
              (fileItem.attachment || fileItem.attachmentInput) && (
                <AttachmentCaptionInput
                  dropdownIconButtonProps={{ size: "extraSmall", shape: "round" }}
                  key={fileItem.id}
                  defaultCaption={fileItem.caption}
                  tooltipProps={{ isEnabled: true, title: "Set caption", placement: "top" }}
                  onSetCaption={(value) => {
                    handleSetCaption(value);
                  }}
                />
              );
            const remove = !fileItem.isUploading && actionsComputed?.remove && (
              <AppTooltip title='Remove' placement='top' isDefaultEnterDelays>
                <AppIconButton size='extraSmall' onClick={handleRemove}>
                  <AppIcon of='clear' />
                </AppIconButton>
              </AppTooltip>
            );

            const isAnyActionVisible =
              loader ||
              cancelUpload ||
              quickView ||
              edit ||
              download ||
              openInNewTab ||
              setCaption ||
              remove;

            return !isAnyActionVisible ? null : (
              <Stack
                className='controls'
                sx={{
                  visibility: fileItem.isUploading ? "visible" : "collapse",
                  display: "flex",
                  position: "absolute",
                  top: 0,
                  right: 0,
                  transform: "scale(1) translate(0%, 0%)",
                  borderRadius: "4px",
                  padding: "2px",
                  pt: "4px",
                  pr: "4px",
                  backgroundColor: (theme) => theme.palette.background.default,
                }}
                direction='row'
                spacing='4px'
              >
                {loader}
                {cancelUpload}
                {quickView}
                {edit}
                {download}
                {openInNewTab}
                {setCaption}
                {remove}
              </Stack>
            );
          })()}
      </Box>
    </AppTooltip>
  );
}
